import { noteOff, parseMessage } from './generate';
import type { MidiEvent, MidiListener, MidiPortInfo, MidiProcessor } from './types';

type PortListener = (ports: { inputs: MidiPortInfo[]; outputs: MidiPortInfo[] }) => void;

/**
 * `unsupported` — the browser has no Web MIDI at all.
 * `denied` — the user or the platform refused the permission.
 * `ready` — access granted; there may still be no devices attached.
 */
export type MidiStatus = 'idle' | 'unsupported' | 'denied' | 'ready';

function describe(port: MIDIPort): MidiPortInfo {
  return {
    id: port.id,
    name: port.name ?? 'Unknown device',
    manufacturer: port.manufacturer ?? '',
    state: port.state,
  };
}

/**
 * A thin router over the native Web MIDI API.
 *
 * Everything downstream — the synth, the sequencers, the 3D keybed — subscribes
 * here rather than to hardware directly, so events minted locally (screen keys,
 * computer keyboard, playback) travel exactly the same path as events from a
 * controller. Hardware output is opt-in and never receives its own input back.
 */
export class MidiBus {
  private access: MIDIAccess | null = null;
  private listeners = new Set<MidiListener>();
  private portListeners = new Set<PortListener>();
  private processors: MidiProcessor[] = [];
  private boundInputs = new Map<string, MIDIInput>();
  private enabledOutputs = new Set<string>();
  /** The one hardware input being listened to, or `null` for all of them. */
  private selectedInput: string | null = null;

  /** Note number -> the note-on that is still sounding. */
  readonly held = new Map<number, MidiEvent>();

  readonly supported =
    typeof navigator !== 'undefined' && typeof navigator.requestMIDIAccess === 'function';

  status: MidiStatus = 'idle';

  /**
   * Requests MIDI access. Resolves `false` when the browser has no Web MIDI or
   * the user denies the permission prompt — the app stays fully playable on the
   * computer keyboard and the on-screen keybed in that case.
   */
  async init(): Promise<MidiStatus> {
    if (!this.supported) {
      this.status = 'unsupported';
      return this.status;
    }
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
    } catch {
      this.status = 'denied';
      return this.status;
    }
    this.access.onstatechange = () => {
      this.bindInputs();
      this.emitPorts();
    };
    this.bindInputs();
    this.status = 'ready';
    this.emitPorts();
    return this.status;
  }

  /** Subscribes to every message on the bus. Returns an unsubscribe function. */
  subscribe(listener: MidiListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Subscribes to a single message type. */
  on(type: MidiEvent['type'], listener: MidiListener): () => void {
    return this.subscribe((event) => {
      if (event.type === type) listener(event);
    });
  }

  /** Subscribes to one controller number. */
  onControlChange(controller: number, listener: MidiListener): () => void {
    return this.on('cc', (event) => {
      if (event.value === controller) listener(event);
    });
  }

  /**
   * Pairs a handler for note-on with one for note-off, filtered to a note range.
   * Replaces Mizzy's `keyToggleRange`.
   */
  onNoteRange(low: number, high: number, onDown: MidiListener, onUp: MidiListener): () => void {
    return this.subscribe((event) => {
      if (event.value < low || event.value > high) return;
      if (event.type === 'noteon') onDown(event);
      else if (event.type === 'noteoff') onUp(event);
    });
  }

  /** Inserts a transform run on every message before dispatch. Return `null` to drop. */
  addProcessor(processor: MidiProcessor): () => void {
    this.processors.push(processor);
    return () => {
      const index = this.processors.indexOf(processor);
      if (index > -1) this.processors.splice(index, 1);
    };
  }

  /** Publishes an event to every subscriber, and to hardware if it came from software. */
  send(event: MidiEvent): void {
    let next: MidiEvent | null = event;
    for (const processor of this.processors) {
      next = processor(next);
      if (next === null) return;
    }

    if (next.type === 'noteon') this.held.set(next.value, next);
    else if (next.type === 'noteoff') this.held.delete(next.value);

    for (const listener of this.listeners) listener(next);

    if (next.source !== 'hardware') this.toHardware(next);
  }

  /** Note numbers currently sounding, in the order they were pressed. */
  get heldNotes(): number[] {
    return [...this.held.keys()];
  }

  isHeld(note: number): boolean {
    return this.held.has(note);
  }

  /** Releases every sounding note. */
  panic(channel = 1): void {
    for (const note of this.heldNotes) this.send(noteOff(note, 0, channel, 'pointer'));
    this.held.clear();
  }

  listPorts(): { inputs: MidiPortInfo[]; outputs: MidiPortInfo[] } {
    if (!this.access) return { inputs: [], outputs: [] };
    return {
      inputs: [...this.access.inputs.values()].map(describe),
      outputs: [...this.access.outputs.values()].map(describe),
    };
  }

  onPortsChanged(listener: PortListener): () => void {
    this.portListeners.add(listener);
    return () => this.portListeners.delete(listener);
  }

  /**
   * Narrows the bus to a single hardware input, or reopens it to all of them
   * with `null`. Everything minted in software still arrives regardless — this
   * only picks which controller is being listened to.
   */
  useInput(id: string | null): void {
    this.selectedInput = id;
    this.bindInputs();
  }

  get input(): string | null {
    return this.selectedInput;
  }

  /** Enables echoing locally generated messages to a hardware output. */
  useOutput(id: string, enabled = true): void {
    if (enabled) this.enabledOutputs.add(id);
    else this.enabledOutputs.delete(id);
  }

  dispose(): void {
    for (const input of this.boundInputs.values()) input.onmidimessage = null;
    this.boundInputs.clear();
    this.listeners.clear();
    this.portListeners.clear();
    if (this.access) this.access.onstatechange = null;
  }

  private bindInputs(): void {
    if (!this.access) return;

    for (const input of this.access.inputs.values()) {
      const wanted =
        input.state !== 'disconnected' &&
        (this.selectedInput === null || this.selectedInput === input.id);
      const bound = this.boundInputs.has(input.id);

      if (wanted && !bound) {
        input.onmidimessage = (message: MIDIMessageEvent) => {
          if (!message.data) return;
          this.send(parseMessage(message.data, 'hardware'));
        };
        this.boundInputs.set(input.id, input);
      } else if (!wanted && bound) {
        input.onmidimessage = null;
        this.boundInputs.delete(input.id);
      }
    }

    // A port that has gone away entirely never comes back through the loop
    // above, so it is dropped here instead.
    for (const [id, input] of this.boundInputs) {
      if (input.state === 'disconnected') {
        input.onmidimessage = null;
        this.boundInputs.delete(id);
      }
    }
  }

  private toHardware(event: MidiEvent): void {
    if (!this.access || this.enabledOutputs.size === 0) return;
    for (const id of this.enabledOutputs) {
      const output = this.access.outputs.get(id);
      // `send` throws on an invalid payload; a bad message should not take down
      // the whole dispatch loop.
      try {
        output?.send(event.data);
      } catch {
        /* ignore */
      }
    }
  }

  private emitPorts(): void {
    const ports = this.listPorts();
    for (const listener of this.portListeners) listener(ports);
  }
}

export const midi = new MidiBus();
